

import { useToast } from "@/context/ToastContext";
import type { BusinessInfo } from "@/lib/settings";

interface BusinessInfoSectionProps {
  data: BusinessInfo;
  onChange: (data: BusinessInfo) => void;
  onSave: (data: BusinessInfo) => void;
  saving: boolean;
  readOnly?: boolean;
}

export default function BusinessInfoSection({
  data,
  onChange,
  onSave,
  saving,
  readOnly = false,
}: BusinessInfoSectionProps) {
  const { showToast } = useToast();

  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6">
      <h2 className="text-lg font-bold text-stone-800 mb-6 font-serif">
        Business Information
      </h2>

      {/* Business Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Business Name *
          </label>
          <input
            type="text"
            value={data.businessName}
            onChange={(e) =>
              onChange({ ...data, businessName: e.target.value })
            }
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="Shalimar Banquet Hall"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Tagline
          </label>
          <input
            type="text"
            value={data.tagline}
            onChange={(e) => onChange({ ...data, tagline: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="Premium Event Management ERP"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Address
          </label>
          <textarea
            value={data.address}
            onChange={(e) => onChange({ ...data, address: e.target.value })}
            rows={2}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="123 Marine Drive, Mumbai, Maharashtra 400001"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Phone
          </label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => onChange({ ...data, phone: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="+91 98765 43210"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Email
          </label>
          <input
            type="email"
            value={data.email}
            onChange={(e) => onChange({ ...data, email: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="info@shalimar.in"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Website
          </label>
          <input
            type="text"
            value={data.website}
            onChange={(e) => onChange({ ...data, website: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="www.shalimar.in"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            GST Number
          </label>
          <input
            type="text"
            value={data.gstNumber}
            onChange={(e) => onChange({ ...data, gstNumber: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="27AABCU9603R1ZX"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            License Number
          </label>
          <input
            type="text"
            value={data.licenseNumber}
            onChange={(e) =>
              onChange({ ...data, licenseNumber: e.target.value })
            }
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:bg-stone-50 disabled:text-stone-500"
            placeholder="BHT-2026-001"
          />
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-stone-100">
        <button
          onClick={() => onSave(data)}
          disabled={saving || readOnly}
          className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? "Saving..." : readOnly ? "View Only" : "Save Business Info"}
        </button>
      </div>
    </div>
  );
}
